import React, {useState, useEffect, useRef} from "react";
import {
    Box,
    Text,
    Textarea,
    Button,
    useColorModeValue,
    useToast,
    Flex,
    FormControl,
    Tooltip,
    Select,
    Spinner,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Menu,
    MenuButton,
    IconButton,
    MenuList,
    MenuItem,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Checkbox,
} from "@chakra-ui/react";
import { cqAPI, getBid, Auth } from "../../lib/Auth";
import {EditIcon, InfoOutlineIcon} from "@chakra-ui/icons";
import CustomizeAIAssistantHeader from "./CustomizeAIAssistantHeader";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { faEllipsisV, faPlay } from "@fortawesome/free-solid-svg-icons";
import CustomizeAIDetail from "./CustomizeAIDetail";
import moment from "moment/moment";
import {useNavigate} from "react-router-dom";
import CustomizeAIAssistantInstruction from "./CustomizeAIAssistantInstruction";
import { STORAGE_KEY_PREFIX } from "./CustomizeAIAssistant";

const CustomizeAI = () => {
    const bgColor = useColorModeValue("gray.100", "gray.700");
    const [instructions, setInstructions] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [reorganize, setReorganize] = useState(false);
    const [organizedInstructions, setOrganizedInstructions] = useState({});
    const [updatedAt, setUpdatedAt] = useState("");
    const [bid, setBid] = useState("");
    const [vavGreeting, setVavGreeting] = useState('');
    const [vavLocationSelectorGreeting, setVavLocationSelectorGreeting] = useState('');
    const [selectedVoice, setSelectedVoice] = useState("Linda");
    const [selectedEzraVoice, setSelectedEzraVoice] = useState("ara");
    const [ezraVoices, setEzraVoices] = useState({});
    const [isEzra, setIsEzra] = useState(false);
    const [isPlayingEzraSample, setIsPlayingEzraSample] = useState(false);
    const [defaultGreeting, setDefaultGreeting] = useState('');
    const [isGreetingEditing, setIsGreetingEditing] = useState(false);
    const [isVavEnable, setIsVavEnable] = useState(false);
    const [isLocationSelectorEnable, setIsLocationSelectorEnable] = useState(false);
    const [isSavingGreeting, setIsSavingGreeting] = useState(false);
    const [defaultGreetingStart, setDefaultGreetingStart] = useState('');
    const [associatedBusinesses, setAssociatedBusinesses] = useState([]);
    const [disableSmsPostAppointment, setDisableSmsPostAppointment] = useState(false);
    const [ezraVoiceEnabled, setEzraVoiceEnabled] = useState(false);
    const [ezraTextEnabled, setEzraTextEnabled] = useState(false);
    const [autopilotInstructions, setAutopilotInstructions] = useState({});
    const [autopilotInstructionsDraft, setAutopilotInstructionsDraft] = useState(() => {
      try {
        const draft = localStorage.getItem(`${STORAGE_KEY_PREFIX + getBid()}-autopilot`);
        return draft ? JSON.parse(draft) : {};
      } catch {
        return {};
      }
    });
    const skipAutopilotDraftPersistRef = useRef(false);
    const bgBoxColor = useColorModeValue("#ffffff", '#212330');
    const tabColor = useColorModeValue("blackAlpha", 'whiteAlpha');
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {isOpen: isSaveConfirmOpen, onOpen: onSaveConfirmOpen, onClose: onSaveConfirmClose} = useDisclosure()
    const toast = useToast();
    const navigate = useNavigate();

  const [instructionsDraft, setInstructionsDraft] = useState(() => {
    try {
      const draft = localStorage.getItem(STORAGE_KEY_PREFIX + getBid());
      return draft ? JSON.parse(draft) : "";
    } catch {
      return "";
    }
  });


  const fetchAiSettings = async () => {
    try {
      const response = await cqAPI.get("/settings/ai");
      if (response.type === "ok") {
        setDefaultGreetingStart(response?.data?.vav?.start || '');
        setDefaultGreeting(response?.data?.vav?.default_value || '');
        setOrganizedInstructions(response?.data?.organized_instructions || {});
        setInstructions(response?.data?.instructions || "");
        setSelectedVoice(response?.data?.vav?.english_voice_name || "Linda");
        setSelectedEzraVoice(response?.data?.ezra_voice || "ara");
        setEzraVoices(response?.data?.ezra_voices || {
          'ara' : 'Warm and friendly Female', 
          'rex' : 'Confident and clear Male', 
          'sal' : 'Smooth and balanced Neutral', 
          'eve' : 'Energetic and upbeat Female', 
          'leo' : 'Authoritative and strong Male'
        });
        setUpdatedAt(response?.data?.updated || "");
        setBid(response?.data?.bid);
        if (response.data?.vav?.greeting) {
          setVavGreeting(response?.data?.vav?.greeting);
        } else {
          setVavGreeting(response?.data?.vav?.default_value || '')
        }
        if (response.data?.vav?.location_selector_greeting) {
          setVavLocationSelectorGreeting(response?.data?.vav?.location_selector_greeting);
        } else {
          setVavLocationSelectorGreeting(response?.data?.vav?.default_location_selector_greeting || '')
        }
        const fetchedAutopilotInstructions = response?.data?.autopilot_instructions || {};
        setAutopilotInstructions(fetchedAutopilotInstructions);
        if (!Object.keys(autopilotInstructionsDraft || {}).length) {
          setAutopilotInstructionsDraft(fetchedAutopilotInstructions);
        }
        setAssociatedBusinesses(response?.data?.associated_businesses || []);
        setDisableSmsPostAppointment(!!response?.data?.disable_sms_post_appointment);
        setEzraVoiceEnabled(!!response?.data?.ezra_voice_enabled);
        setEzraTextEnabled(!!response?.data?.ezra_text_enabled);
        setIsEzra(!!response?.data?.is_ezra);
        setIsVavEnable(response.data.vav.enabled);
        setIsLocationSelectorEnable(response.data.vav.location_selector_enabled)
      }
    } catch (error) {
      console.error("Error loading voice auto pilot greeting:", error);
      toast({
        title: "Error",
        description: "Failed to load voice auto pilot greeting.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };
    useEffect(() => {
        fetchAiSettings();
    }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_PREFIX + getBid(), JSON.stringify(instructionsDraft));
    } catch {
      console.error("Failed to save Drafts.");
    }
  }, [instructionsDraft]);

  useEffect(() => {
    if (skipAutopilotDraftPersistRef.current) {
      skipAutopilotDraftPersistRef.current = false;
      return;
    }
    try {
      localStorage.setItem(
        `${STORAGE_KEY_PREFIX + getBid()}-autopilot`,
        JSON.stringify(autopilotInstructionsDraft)
      );
    } catch {
      console.error("Failed to save autopilot drafts.");
    }
  }, [autopilotInstructionsDraft]);

    useEffect(() => {
        const draft = localStorage.getItem(STORAGE_KEY_PREFIX + getBid());
        draft && setInstructionsDraft(JSON.parse(draft));
    }, [])

    const handleSaveGreeting = async () => {
        try {
            setIsSavingGreeting(true);
            const response = await cqAPI.post("/settings/ai", {
                greeting: vavGreeting,
                location_selector_greeting: vavLocationSelectorGreeting,
                english_voice_name: selectedVoice,
        ezra_voice: selectedEzraVoice,
            });

            if (response.type === "ok") {
                toast({
                    title: response.msg || "Saved successfully.",
                    description: "Your voice auto pilot greeting have been saved.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                setIsGreetingEditing(false);
            } else {
                toast({
                    title: "Error",
                    description: response.msg || 'Failed to save!',
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "Error",
                description: error.msg,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
        setIsSavingGreeting(false);
    };

    const handleVoiceChange = (value) => setSelectedVoice(value);

    const handleGreetingChange = async (value) => {
        setVavGreeting(value)
    }
    const handleLocationSelectorGreetingChange = async (value) => {
        setVavLocationSelectorGreeting(value)
    }

  const handlePlayEzraSample = async () => {
    if (isPlayingEzraSample) return;
    setIsPlayingEzraSample(true);
    let audioContext;
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const response = await cqAPI.get(`/ezra/voice/sample`, { voice: selectedEzraVoice }, 15000);
      if (response.type !== 'ok' || !response.audio) throw new Error(response.msg || 'No audio returned');

      // Decode base64 WAV to ArrayBuffer
      const binaryString = atob(response.audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const audioBuffer = await audioContext.decodeAudioData(bytes.buffer);
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      source.onended = () => setIsPlayingEzraSample(false);
      source.start();
    } catch (error) {
      console.error('Error playing voice sample:', error);
      if (audioContext) audioContext.close();
      toast({
        title: "Error",
        description: "Failed to play voice sample.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setIsPlayingEzraSample(false);
    }
  };

  const handleSubmit = async () => {
    onSaveConfirmClose();
    try {
      setIsSubmitting(true);
      const res = await cqAPI.post("/settings/ai", {
        instructions: instructionsDraft,
        autopilot_instructions: autopilotInstructionsDraft,
        reorganize,
      });
      if (res.type === "ok") {
        toast({
          title: "Updated!",
          description: "AI settings updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setIsEditing(false)
        setIsSubmitting(false);
        setReorganize(false);
        setInstructionsDraft("");
        fetchAiSettings();
      } else {
        setIsSubmitting(false);
        toast({
          title: "Error",
          description: res.msg || "Failed to save.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (e) {
      setIsSubmitting(false);
      toast({
        title: "Error",
        description: e?.msg || "Unexpected error while configuring.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

    return (
      <Box m={4} px={4} width="100%" height="100%" overflowY="auto" backgroundColor={bgBoxColor} borderRadius="8px">
        <Tabs isFitted variant='line' width='full' colorScheme={tabColor} isLazy>
          <TabList width="75%">
            <Tab>Set Up AI</Tab>
            <Tab>Voice Auto Pilot Setup</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              {/*<CustomizeAIAssistantHeader draft={instructionsDraft}/>*/}
              <Box mt={4} p={1} bgColor={bgColor} borderRadius="md" width="full">
                <Flex align="center" minH="90px" flexDirection="column" alignItems="start">
                  <Flex flexDirection="row" alignItems="center" width="100%">
                    <Flex justifyContent="flex-start" flexDirection="column" mt={2} alignItems='start'>
                      <Text fontSize="sm" fontWeight="bold" ml={2}>
                        AI Settings
                      </Text>
                      {updatedAt &&
                        <Text fontSize="sm" ml={2} mt={1}>
                          Updated: {moment(updatedAt).format("MMMM D, YYYY h:mm a")}
                        </Text>
                      }
                    </Flex>
                    <Flex justifyContent="flex-end" ml='auto' gap={2} my={1} mr={2} minHeight="40px"
                          alignItems="center">
                      {isEditing && (
                        <>
                          <Button
                            isDisabled={!isEditing || isSubmitting}
                            size="sm"
                            onClick={() => {
                              setIsEditing(false)
                              setInstructionsDraft("")
                              setAutopilotInstructionsDraft(autopilotInstructions)
                              skipAutopilotDraftPersistRef.current = true;
                              try {
                                localStorage.removeItem(`${STORAGE_KEY_PREFIX + getBid()}-autopilot`);
                              } catch {
                                console.error("Failed to clear autopilot drafts.");
                              }
                            }}>Cancel</Button>
                          <Button
                            isDisabled={!isEditing || isSubmitting}
                            size="sm"
                            onClick={onSaveConfirmOpen}> {isSubmitting ?
                            <Spinner/> : "Save"}
                          </Button>
                        </>)}
                      <Menu flexBasis="5%">
                        <MenuButton as={IconButton} aria-label="Options"
                                    icon={<FontAwesomeIcon icon={faEllipsisV}/>}
                                    variant="ellipsis"/>
                        <MenuList>
                          {!isEditing &&
                            <MenuItem onClick={() => setIsEditing(true)}>
                              Edit
                            </MenuItem>
                          }
                          <MenuItem
                            onClick={() => navigate(`/settings/customize-ai/history?bid=${bid}`)}>
                            Setup History
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Flex>
                  </Flex>
                  <Flex minH="90px" flexDirection="column" gap={2} width="100%">
                    <Flex flexDirection="column" align="start" m={4}>
                      <Text fontSize="sm" fontWeight="bold" mb={2}>
                        General Instruction
                      </Text>
                      <Textarea
                        value={isEditing ? instructionsDraft || instructions : instructions}
                        isDisabled={!isEditing || isSubmitting}
                        height="400px"
                        onChange={(e) => {
                          setInstructionsDraft(e.target.value)
                        }}/>
                    </Flex>
                    <Flex flexDirection="column" align="start" m={4}>
                      <Text fontSize="sm" fontWeight="bold" mb={2}>
                        Routing Instruction
                      </Text>
                      <Textarea
                        value={isEditing ? (autopilotInstructionsDraft.routing || '') : (autopilotInstructions.routing || '')}
                        isDisabled={!isEditing || isSubmitting}
                        height="200px"
                        placeholder="Enter your specific routing instructions for auto pilot here"
                        onChange={(e) => {
                          setAutopilotInstructionsDraft({
                            ...autopilotInstructionsDraft,
                            routing: e.target.value,
                          })
                        }}/>
                    </Flex>
                  </Flex>
                </Flex>
              </Box>
              {Auth.user?.isSuperAdmin && (
              <Box mt={4} p={1} bgColor={bgColor} borderRadius="md" width="full">
                <Flex align="center" minH="90px" flexDirection="column" alignItems="start">
                  <Flex flexDirection="row" alignItems="center" width="100%">
                    <Flex justifyContent="flex-start" flexDirection="column" mt={2} alignItems='start'>
                      <Text fontSize="sm" fontWeight="bold" ml={2}>
                        AI Details
                      </Text>
                    </Flex>
                      <Flex justifyContent="flex-end" ml='auto' gap={2} my={1} mr={2} minHeight="40px"
                          alignItems="center">

                      <Menu flexBasis="5%">
                        <MenuButton as={IconButton} aria-label="Options"
                                    icon={<FontAwesomeIcon icon={faEllipsisV}/>}
                                    variant="ellipsis"/>
                        <MenuList>
                          <MenuItem onClick={onOpen}>
                            Edit
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Flex>
                  </Flex>
                  <CustomizeAIDetail organized_instructions={organizedInstructions} associatedBusinesses={associatedBusinesses} disableSmsPostAppointment={disableSmsPostAppointment} ezraVoiceEnabled={ezraVoiceEnabled} ezraTextEnabled={ezraTextEnabled}/>
                </Flex>
              </Box>
              )}
            </TabPanel>
            <TabPanel>
              <Box p={1} bgColor={bgColor} borderRadius="md" width="full">
                <Flex align="center" minH="200px" flexDirection="column" alignItems="start">
                  <Flex flexDirection="row" alignItems="center" width="100%">
                    <Flex justifyContent="flex-start">
                      <Text fontSize="sm" fontWeight="bold" mb={2} ml={2}>
                        Voice Auto Pilot Greeting:
                      </Text>
                    </Flex>
                    <Flex justifyContent="flex-end" ml='auto' gap={2} mb={2} minHeight="40px"
                          alignItems="center">
                      {isVavEnable && isGreetingEditing ? (
                          <>
                            <Button id={"reset-greeting"}
                                    onClick={() => setVavGreeting(defaultGreeting)}
                                    size="sm" variant="ghost" mr={3} mt={1}>Reset to
                              Default</Button>
                            <Button isDisabled={isSavingGreeting} size="sm" id={"cancel-greeting"}
                                    onClick={() => setIsGreetingEditing(false)}>cancel</Button>
                            <Button isDisabled={isSavingGreeting} size="sm" id={"save-greeting"}
                                    onClick={handleSaveGreeting}> {isSavingGreeting ?
                              <Spinner/> : "Save"}
                            </Button>
                          </>)
                        :
                        isVavEnable && (
                          <EditIcon id={"edit-greeting"} mr={1}
                                    onClick={() => isVavEnable && setIsGreetingEditing(true)}/>)
                      }
                    </Flex>
                  </Flex>
                  {/* Greeting textarea */}
                  <Tooltip
                    label={isVavEnable ? "Select edit to make changes" : "You need to enable autopilot feature."}
                    aria-label="A tooltip"
                    isDisabled={isGreetingEditing}
                    placement="top-end"
                    mr={6}
                  >
                    <Textarea
                      isDisabled={!isGreetingEditing}
                      placeholder="Enter your voice auto pilot greeting here..."
                      id="vav_greeting"
                      minH="120px"
                      value={vavGreeting}
                      onChange={(e) => handleGreetingChange(e.target.value)}
                    />
                  </Tooltip>
                    {isVavEnable && isLocationSelectorEnable && (
                      <>
                        {/* Location Selector Greeting textarea */}
                        <Flex justifyContent="flex-start">
                          <Text fontSize="sm" fontWeight="bold" mb={2} ml={2}>
                            Voice Auto Pilot Location Selector Greeting:
                          </Text>
                        </Flex>

                        <Tooltip
                          label="Select edit to make changes"
                          aria-label="A tooltip"
                          isDisabled={isGreetingEditing}
                          placement="top-end"
                          mr={6}
                        >
                          <Textarea
                            isDisabled={!isGreetingEditing}
                            placeholder="Enter your voice auto pilot location selector greeting here..."
                            id="vav_greeting"
                            minH="120px"
                            value={vavLocationSelectorGreeting}
                            onChange={(e) =>
                              handleLocationSelectorGreetingChange(e.target.value)
                            }
                          />
                        </Tooltip>
                      </>
                    )}
                  {/* Vav Voice selector — hidden for Ezra practices */}
                  {!isEzra && (
                  <Box mt={6} width="100%">
                    <Flex justifyContent="flex-start">
                      <Text fontSize="sm" fontWeight="bold" mb={2} ml={2}>
                        Auto Pilot Voice:
                      </Text>
                    </Flex>
                    <FormControl mt={4} width="100%" isDisabled={!isGreetingEditing}>
                      <Select
                        id="vav_voice_select"
                        value={selectedVoice}
                        onChange={(e) => handleVoiceChange(e.target.value)}
                        placeholder="Choose a voice"
                      >
                        <option value="Linda">Linda</option>
                        <option value="Katie">Katie</option>
                        <option value="Savanah">Savanah</option>
                      </Select>
                    </FormControl>
                  </Box>
                  )}
                {/* Ezra Voice selector — only shown for practices using Ezra */}
                {isEzra && (
                <Box mt={6} width="100%">
                  <Flex justifyContent="flex-start">
                    <Text fontSize="sm" fontWeight="bold" mb={2} ml={2}>
                Voice:
                    </Text>
                  </Flex>
                  <FormControl mt={4} width="100%" isDisabled={!isGreetingEditing}>
                    <Select
                      id="ezra_voice_select"
                      value={selectedEzraVoice}
                      onChange={(e) => setSelectedEzraVoice(e.target.value)}
                      placeholder="Choose an Ezra voice"
                    >
                      {Object.keys(ezraVoices).map((k) => (
                        <option key={k} value={k}>{ezraVoices[k]}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <Flex mt={2} ml={2} alignItems="center">
                    <Button
                      size="sm"
                      variant="ghost"
                      isLoading={isPlayingEzraSample}
                      loadingText="Playing..."
                      onClick={handlePlayEzraSample}
                      leftIcon={<FontAwesomeIcon icon={faPlay} />}
                    >
                      Play Sample
                    </Button>
                  </Flex>
                </Box>
                )}
                </Flex>
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
        <Modal onClose={onClose} isOpen={isOpen} size="lg">
          <ModalOverlay/>
                <ModalContent>
                    <ModalHeader>Edit AI Settings</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody color="#B53D37" fontWeight="bold" minHeight="150px">
                        We highly recommend using the AI setup assistant to update your settings. Would you like to
                        continue?
                    </ModalBody>
                    <ModalFooter mr={4}>
                        <Button variant="ghost" onClick={onClose} mr={2}>Cancel</Button>
                        <Button
                            onClick={() => {
                                onClose()
                                navigate(`/settings/customize-ai/edit?bid=${bid}`);
                            }} minWidth="80px">Yes</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        <Modal onClose={() => { onSaveConfirmClose(); setReorganize(false); }} isOpen={isSaveConfirmOpen} size="lg">
          <ModalOverlay/>
          <ModalContent>
            <ModalHeader>Update AI Instructions</ModalHeader>
            <ModalCloseButton/>
            <ModalBody>
              <Text mb={4}>
                Are you sure you want to update your AI Settings? This will affect your voice and messaging autopilot behavior.
              </Text>
              <Flex alignItems="center" gap={2}>
                <Checkbox
                  size="sm"
                  isChecked={reorganize}
                  onChange={(e) => setReorganize(e.target.checked)}
                >
                  <Text fontSize="sm">Reprocess Instructions</Text>
                </Checkbox>
                <Tooltip
                  label="Turn this on when making major changes to your instructions (e.g., adding new rules, changing how calls are handled). The AI will reanalyze your instructions to update its behavior. Leave this off for minor edits like fixing typos or rewording."
                  placement="right"
                >
                  <InfoOutlineIcon boxSize={3} color="gray.500" cursor="pointer" />
                </Tooltip>
              </Flex>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={() => { onSaveConfirmClose(); setReorganize(false); }} mr={2}>Cancel</Button>
              <Button onClick={handleSubmit} minWidth="80px">Confirm</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        </Box>
    )
};

export default CustomizeAI;
